import { useEffect, useState } from "react";
import { getClientsByEfileStatuses } from "../../services/analytics/getClientsByEfileStatuses";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import SelectYearEfileStatusReportListBox from "../../components/analytics/SelectYearEfileStatusReportListBox";
import { getSubscriptionYears } from "../../services/analytics/getSubscriptionYears";
import PageContainer from "../../components/PageContainer"; // 👈 agrega esto
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

const EFILE_STATUSES = ["Needs Extension", "On Extension", "Accepted", "Rejected"];

const EfileStatusReport = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [clientsByEfileStatuses, setClientsByEfileStatuses] = useState([]);
    const [currentTaxYear, setCurrentTaxYear] = useState(0);
    const [selectedYear, setSelectedYear] = useState(null);
    const [years, setYears] = useState([
        { name: currentTaxYear, year: currentTaxYear },
    ]);

    const [isLoading, setIsLoading] = useState(true); // 👈 loader
    const [error, setError] = useState(null); // 👈 error global

    const getReport = async (year) => {
        const response = await getClientsByEfileStatuses(year);
        if (response.success) {
            // console.log(response.data.clientsByEfileStatuses);
            setClientsByEfileStatuses(response.data.clientsByEfileStatuses);
        } else {
            // Deja de esconder el error bajo la alfombra 😅
            throw new Error(
                response.message || "Failed to fetch e-file status report",
            );
        }
    };

    const getReportFromThisYear = async () => {
        const year = new Date().getFullYear() - 1;
        setCurrentTaxYear(year);
        setSelectedYear({ name: year, year: year });
        await getReport(year); // 👈 importante: await para que el loader espere
    };

    const getDifferentSubscriptionYears = async () => {
        const response = await getSubscriptionYears();
        if (response.success) {
            const newYears = response.data.years.map((year) => ({
                name: year,
                year: year,
            }));
            newYears.push({ name: "All", year: 0 });
            setYears(newYears);
        } else {
            throw new Error(
                getClientServiceMessage(
                    response.message,
                    "Failed to fetch service years",
                ),
            );
        }
    };

    useEffect(() => {
        let isMounted = true;

        async function loadInitialData() {
            try {
                setIsLoading(true);
                setError(null);

                await Promise.all([
                    getReportFromThisYear(),
                    getDifferentSubscriptionYears(),
                ]);
            } catch (err) {
                if (!isMounted) return;
                setError(err);
            } finally {
                if (!isMounted) return;
                setIsLoading(false);
            }
        }

        loadInitialData();

        return () => {
            isMounted = false;
        };
    }, []);

    const onHandleChangeYear = async (option) => {
        console.log(option.year);
        setSelectedYear(option);

        try {
            setIsLoading(true);
            setError(null);
            await getReport(option.year);
        } catch (err) {
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            loadingText="Loading e-file status report..."
            showOverlay={true}
        >
            <section>
                <div className="flex h-screen overflow-hidden">
                    {/* Sidebar */}
                    <Sidebar
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    {/* Content area */}
                    <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                        {/*  Site header */}
                        <Header
                            sidebarOpen={sidebarOpen}
                            setSidebarOpen={setSidebarOpen}
                        />

                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                {/* Page header */}
                                <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                    {/* Left: Title */}
                                    <div className="mb-4 sm:mb-0">
                                        <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                            E-File Status{" "}
                                            {selectedYear && selectedYear.name}
                                        </h1>
                                    </div>
                                    <div className="flex flex-col items-center sm:flex-row sm:space-x-2">
                                        <div className="flex flex-col items-start">
                                            {selectedYear !== null && (
                                                <SelectYearEfileStatusReportListBox
                                                    label={"Select year"}
                                                    onChange={onHandleChangeYear}
                                                    years={years}
                                                    selectedYear={selectedYear}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {/* Right: Actions */}
                                </div>
                            </div>

                            <div className="flex flex-row mb-12 overflow-x-auto">
                                {clientsByEfileStatuses.map((report, index) => {
                                    return (
                                        <div
                                            key={index} // 👈 key en el contenedor, no en el <ul>
                                            className="w-full min-h-full p-4 mx-4 bg-white border border-gray-200 rounded-lg shadow sm:p-8 dark:border-gray-700 min-w-fit"
                                        >
                                            <div className="flex items-center justify-between mb-4">
                                                <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-gray-800">
                                                    {
                                                        EFILE_STATUSES[
                                                            report.efileStatus
                                                        ]
                                                    }
                                                </h5>
                                                <p className="text-sm font-medium text-green-600 hover:underline dark:text-green-500">
                                                    Total: {report.clients.length}
                                                </p>
                                            </div>
                                            <div className="flow-root">
                                                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                                    {report.clients.length > 0 ? (
                                                        report.clients.map(
                                                            (client, idx) => (
                                                                <li
                                                                    className="py-3 sm:py-4"
                                                                    key={idx}
                                                                >
                                                                    <div className="flex items-center">
                                                                        <div className="flex-shrink-0"></div>
                                                                        <div className="flex-1 min-w-0 ms-4">
                                                                            <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-700">
                                                                                <a
                                                                                    href={`/clients/service/${client.serviceId}/client/${client.id}`}
                                                                                    rel="noreferrer"
                                                                                >
                                                                                    Client:{" "}
                                                                                    {
                                                                                        client.name
                                                                                    }
                                                                                </a>
                                                                            </p>
                                                                            <p className="text-sm text-gray-500 truncate dark:text-gray-400">
                                                                                Service
                                                                                Name:{" "}
                                                                                {
                                                                                    client.serviceName
                                                                                }
                                                                            </p>
                                                                            <p className="text-sm text-gray-500 truncate dark:text-gray-400">
                                                                                Service
                                                                                Year:{" "}
                                                                                {
                                                                                    client.serviceYear
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            ),
                                                        )
                                                    ) : (
                                                        <li className="py-3 sm:py-4">
                                                            No services assigned to
                                                            this E file Status.
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </main>
                    </div>
                </div>
            </section>
        </PageContainer>
    );
};

export default EfileStatusReport;
